import React, { useState, useRef, useEffect, useCallback } from 'react';
import ImageLightbox from '../components/ImageLightbox';
import MetaItem from '../components/MetaItem';
import TagList from '../components/TagList';
import BackButton from '../components/BackButton';
import HomeButton from '../components/HomeButton';
import CarouselControls from '../components/CarouselControls';
import { sideProjects } from '../data/siteData';
import { updateCarouselDots } from '../utils/carouselDots';
import './BookCoverProject.css';

const project = sideProjects.find(p => p.url === '/side-projects/book-cover');

const rounds = [
  {
    title: 'Original',
    images: [
      { src: '/images/book-cover/r0.png', alt: 'Original cover by Bryant Rueda', caption: "Original (author's contribution)" },
    ],
  },
  {
    title: 'Round 1 — Improving the art and typography',
    images: [
      { src: '/images/book-cover/r1-1.png', alt: 'Round 1 — 1', caption: 'Initial revision' },
      { src: '/images/book-cover/r1-3.png', alt: 'Round 1 — 3', caption: 'Art opacity and solid color' },
    ],
  },
  {
    title: 'Round 2 — Updating the subtitle to better reflect the contents',
    images: [
      { src: '/images/book-cover/r2-2.png', alt: 'Round 2 — 2', caption: '1 — Subtitle adjustment' },
      { src: '/images/book-cover/r2-3.png', alt: 'Round 2 — 3', caption: '2 — Subtitle adjustment' },
    ],
  },
  {
    title: 'Final',
    wide: true,
    images: [
      { src: '/images/book-cover/final-1.png', alt: 'Final cover with fold guides', caption: '1 — With fold guides' },
      { src: '/images/book-cover/final-2.png', alt: 'Final cover', caption: '2 — Print-ready' },
    ],
  },
  {
    title: 'Repurposing',
    wide: true,
    images: [
      { src: '/images/book-cover/r3-1.png', alt: 'Repurposing — Full KDP cover spread', caption: 'Full wrap-around cover (front, spine, back)' },
    ],
  },
];

const allImages = rounds.flatMap(r => r.images.map(img => ({ ...img, wide: !!r.wide })));

const roundStartIndices = rounds.reduce((acc, round, i) => {
  acc.push(i === 0 ? 0 : acc[i - 1] + rounds[i - 1].images.length);
  return acc;
}, []);


const BookCoverProject = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);
  const itemRefs = useRef([]);
  const dotRefs = useRef([]);
  const prevDotIndex = useRef(0);

  const updateScales = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const containerCenter = container.scrollLeft + container.offsetWidth / 2;
    let closestIndex = 0;
    let closestDistance = Infinity;
    const itemCenters = [];

    itemRefs.current.forEach((item, i) => {
      if (!item) return;
      const itemCenter = item.offsetLeft + item.offsetWidth / 2;
      itemCenters[i] = itemCenter;
      const distance = Math.abs(itemCenter - containerCenter);
      const maxDist = container.offsetWidth * 0.7;
      const signedDist = itemCenter - containerCenter;
      const tScale = Math.min(distance / maxDist, 1);
      const scale = 1 - tScale * 0.6;
      const opacity = 1 - tScale * 0.5;
      const rotateY = (signedDist / maxDist) * 35;
      item.style.transform = `perspective(600px) rotateY(${rotateY}deg) scale(${scale})`;
      item.style.opacity = opacity;

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });

    // Fractional position between the two straddle snap points
    let fromIdx = 0;
    const len = itemCenters.filter(c => c !== undefined).length;
    for (let i = 0; i < len - 1; i++) {
      if (itemCenters[i] !== undefined && itemCenters[i] <= containerCenter) fromIdx = i;
    }
    const toIdx = Math.min(fromIdx + 1, len - 1);
    const dotT = (fromIdx === toIdx || itemCenters[toIdx] === undefined) ? 0 :
      Math.max(0, Math.min(1, (containerCenter - itemCenters[fromIdx]) /
                               (itemCenters[toIdx] - itemCenters[fromIdx])));

    updateCarouselDots(dotRefs, fromIdx, toIdx, dotT);

    if (closestIndex !== prevDotIndex.current) {
      prevDotIndex.current = closestIndex;
      setActiveIndex(closestIndex);
    }
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.addEventListener('scroll', updateScales, { passive: true });
    updateScales();
    return () => container.removeEventListener('scroll', updateScales);
  }, [updateScales]);


  const scrollToIndex = (index) => {
    const clamped = Math.max(0, Math.min(allImages.length - 1, index));
    const item = itemRefs.current[clamped];
    const container = scrollRef.current;
    if (!item || !container) return;
    const itemCenter = item.offsetLeft + item.offsetWidth / 2;
    container.scrollTo({ left: itemCenter - container.offsetWidth / 2, behavior: 'smooth' });
  };

  return (
    <>
    <BackButton />
    <HomeButton />
    <div className="book-cover-page">
      {lightboxIndex !== null && (
        <ImageLightbox
          images={allImages}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      <div className="page-container one-col-layout">
        <div className="case-study-hero">
          <h1>{project.title}</h1>
          <p className="subtitle">{project.description}</p>
          <TagList tags={project.tags} className="case-study-tags" />
          <div className="case-study-meta-row">
            <MetaItem label="Year" value={project.year} />
            <MetaItem label="Tools" value={project.tools} />
          </div>
        </div>
      </div>

      {/* Carousel — full bleed outside page-container */}
      <div className="bc-carousel-wrapper">
        <div className="bc-carousel" ref={scrollRef}>
          {allImages.map((img, i) => (
            <div
              key={img.src}
              ref={el => { itemRefs.current[i] = el; }}
              className={`bc-carousel-item${img.wide ? ' bc-carousel-item--wide' : ''}`}
              onClick={() => setLightboxIndex(i)}
            >
              <div className="bc-carousel-cover">
                <img src={img.src} alt={img.alt} />
              </div>
              <p className="bc-carousel-caption">{img.caption}</p>
            </div>
          ))}
        </div>

        {/* Timeline indicator */}
        <div className="bc-timeline">
          <span className="bc-timeline-phase overline">
            {rounds.find((_, ri) =>
              activeIndex >= roundStartIndices[ri] &&
              activeIndex < roundStartIndices[ri] + rounds[ri].images.length
            )?.title}
          </span>
          <CarouselControls
            count={allImages.length}
            activeIndex={activeIndex}
            dotRefs={dotRefs}
            getLabel={(i) => allImages[i].caption}
            onDotClick={scrollToIndex}
            onPrev={() => scrollToIndex(activeIndex - 1)}
            onNext={() => scrollToIndex(activeIndex + 1)}
            prevDisabled={activeIndex === 0}
            nextDisabled={activeIndex === allImages.length - 1}
          />
        </div>
      </div>
    </div>
    </>
  );
};

export default BookCoverProject;
